/** @file PuppetsScreen: a project's puppet cards (kind, scope, revision), New puppet, Trash tab, menus. */
import { useCallback, useState } from 'react';
import type { ReactElement } from 'react';
import { useApi } from '../shell/useApi';
import { useProjectName } from '../shell/useProjectName';
import type { EditorStore } from '../state/editorTypes';
import { useEditor } from '../state/useEditor';
import { useEditorStore } from '../state/useEditorStore';
import type { EditorApi, PuppetEntry } from '../store/editorApi';
import { Button } from '../ui-kit/Button';
import { Card } from '../ui-kit/Card';
import { Spinner } from '../ui-kit/Spinner';
import { Tabs } from '../ui-kit/Tabs';
import { entryCardMenu } from './entryCardMenu';
import { NewPuppetModal } from './NewPuppetModal';
import { runAndReload } from './runAndReload';
import { scopeSummary } from './scopeSummary';
import { useServerList } from './useServerList';

type Tab = 'puppets' | 'trash';

const TABS: { id: Tab; label: string }[] = [{ id: 'puppets', label: 'Puppets' }, { id: 'trash', label: 'Trash' }];

/** The menu of one puppet card; rename is the `puppet.rename` command (the name lives in the document). */
function cardMenu(store: EditorStore, api: EditorApi, p: PuppetEntry, trashed: boolean, reload: () => Promise<void>) {
  return entryCardMenu(trashed, {
    open: () => { store.getState().navigate({ screen: 'pose', puppetId: p.id }); },
    rename: () => {
      const name = globalThis.prompt('Rename puppet', p.name)?.trim();
      if (name === undefined || name === '' || name === p.name) return;
      runAndReload(store, () => api.puppets.commands(p.id, { commands: [{ name: 'puppet.rename', input: { name } }] }), reload)();
    },
    trash: runAndReload(store, () => api.puppets.trash(p.id), reload),
    restore: runAndReload(store, () => api.puppets.restore(p.id), reload),
    purge: () => {
      if (globalThis.confirm(`Purge "${p.name}" for good? This cannot be undone.`)) runAndReload(store, () => api.puppets.purge(p.id), reload)();
    },
  });
}

/** The screen head: back to the projects, the project's name, the list tabs and (outside the trash) New puppet. */
function Head({ title, tab, onTab, onNew }: { title: string; tab: Tab | null; onTab: (t: Tab) => void; onNew: (() => void) | null }): ReactElement {
  const navigate = useEditor((s) => s.navigate);
  return (
    <div className="p3-screen-head">
      <Button variant="ghost" onClick={() => { navigate({ screen: 'projects' }); }}>Projects</Button>
      <span className="p3-crumb-sep">›</span>
      <h1>{title}</h1>
      {tab === null ? null : <Tabs label="Puppet lists" tabs={TABS} active={tab} onSelect={onTab} />}
      <span className="p3-spacer" />
      {onNew === null ? null : <Button variant="primary" onClick={onNew}>New puppet</Button>}
    </div>
  );
}

/** Without a server the project holds the one puppet in memory. */
function MemoryPuppets({ projectId }: { projectId: string }): ReactElement {
  const store = useEditorStore();
  const doc = useEditor((s) => (s.puppet.projectId === projectId ? s.puppet.doc : null));
  return (
    <div className="p3-screen">
      <Head title={projectId} tab={null} onTab={() => undefined} onNew={null} />
      {doc === null ? <p className="p3-muted">No puppets in memory for this project.</p> : (
        <div className="p3-grid">
          <Card title={doc.name} subtitle={`${doc.kind} · in memory · r${String(doc.revision)}`}
            onOpen={() => { store.getState().navigate({ screen: 'pose', puppetId: doc.id }); }} />
        </div>
      )}
    </div>
  );
}

/** The project's puppets (live or trashed) from the server; click opens the Pose screen, right-click the menu. */
function ServerPuppets({ api, projectId }: { api: EditorApi; projectId: string }): ReactElement {
  const store = useEditorStore();
  const [tab, setTab] = useState<Tab>('puppets');
  const creating = useEditor((s) => s.shell.dialog === 'newPuppet');
  const setCreating = (open: boolean): void => { store.getState().setShell({ dialog: open ? 'newPuppet' : null }); };
  const trashed = tab === 'trash';
  const list = useServerList(useCallback(() => api.puppets.list({ project: projectId, ...(trashed ? { trashed: true } : {}) }), [api, projectId, trashed]));
  const name = useProjectName(api, projectId);
  const open = (id: string) => (): void => { store.getState().navigate({ screen: 'pose', puppetId: id }); };
  return (
    <div className="p3-screen">
      <Head title={name} tab={tab} onTab={setTab} onNew={trashed ? null : () => { setCreating(true); }} />
      {list.error === null ? null : <p className="p3-error" role="alert">{list.error}</p>}
      {list.entries === null ? <Spinner /> : null}
      {list.entries?.length === 0 ? <p className="p3-muted">{trashed ? 'The trash is empty.' : 'No puppets yet: a puppet is one character, enemy, weapon or prop.'}</p> : null}
      <div className="p3-grid">
        {(list.entries ?? []).map((p) => (
          <Card key={p.id} title={p.name} subtitle={`${p.kind} · ${scopeSummary(p.scope)} · r${String(p.revision)}`}
            onOpen={trashed ? undefined : open(p.id)}
            onMenu={(x, y) => { store.getState().openMenu(x, y, cardMenu(store, api, p, trashed, list.reload)); }} />
        ))}
      </div>
      {creating ? <NewPuppetModal api={api} projectId={projectId} onClose={() => { setCreating(false); }} /> : null}
    </div>
  );
}

/** The puppets of project `projectId` (the route's), from the server or the memory store. */
export function PuppetsScreen({ projectId }: { projectId: string }): ReactElement {
  const api = useApi();
  return api === null ? <MemoryPuppets projectId={projectId} /> : <ServerPuppets api={api} projectId={projectId} />;
}
